import * as React from "react";
import Button from "@mui/material/Button";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { IActionButtons, IActionButtonSettings } from "modules/common/models";
import { EventType } from "modules/events/models";
import { actionButtonSettingsAreValid } from "utils/actionButtonSettingsAreValid";

const primaryButtonClassName = "action-button-primary";

export const ActionButtons: React.FC<ComponentProps> = ({
    respondingEnabled,
    isCurrentUserAttendingInPerson,
    isCurrentUserAttendingOnline,
    isCurrentUserOnWaitlist,
    isCurrentUserNotAttending,
    isResponseEditable,
    eventType,
    actionButtons,
    onPrimaryButtonClick,
}) => {
    if (!respondingEnabled)
        return <></>;

    const userHasResponded = isCurrentUserAttendingInPerson ||
        isCurrentUserAttendingOnline ||
        isCurrentUserOnWaitlist ||
        isCurrentUserNotAttending;

    const getActionButton = (buttonSettings: IActionButtonSettings, isPrimary: boolean): React.ReactNode | undefined => {
        // for the action button to be shown, it must be active, and have at least a link or text.
        const showActionButton = actionButtonSettingsAreValid(buttonSettings);
        
        // if the display text is empty, show the first 20 chars of the url instead
        const buttonDisplayText = buttonSettings.text
            ? buttonSettings.text 
            : buttonSettings.link.slice(0, 20);

        return showActionButton &&
            <Button 
                color="primary"
                variant={isPrimary ? "contained" : "text"}
                onClick={() =>
                    buttonSettings.link &&
                    window.open(
                        `${buttonSettings.link}`,
                        `${buttonSettings.openInNewWindow ? "_blank" : "_self"}`, 
                        "noopener,noreferrer"
                    )
                }
                className={isPrimary ? primaryButtonClassName : undefined}
            >
                {buttonDisplayText}
            </Button>
    }

    const getPrimaryButton = (): React.ReactNode | undefined => {
        if (eventType === "informational") {
            return getActionButton(actionButtons.primaryButtonSettings, true);
        }
        else {
            return <Button 
                color="primary"
                variant="contained"
                endIcon={<ArrowRightIcon /> } 
                onClick={onPrimaryButtonClick}
                disabled={!isResponseEditable}
                className={primaryButtonClassName}
            >
                {userHasResponded ? "Edit RSVP" : "RSVP Now"}
            </Button>
        }
    }

    return <div className="action-buttons">
        {getActionButton(actionButtons.secondaryButtonSettings, false)}
        {getPrimaryButton()}
    </div>;
}

interface ComponentProps {
    respondingEnabled?: boolean;
    isCurrentUserAttendingInPerson?: boolean;
    isCurrentUserAttendingOnline?: boolean;
    isCurrentUserNotAttending?: boolean;
    isCurrentUserOnWaitlist?: boolean;
    isResponseEditable?: boolean;
    eventType: EventType;
    actionButtons: IActionButtons;
    onPrimaryButtonClick?: () => void;
}
